import React, { FunctionComponent, useCallback } from 'react';
import { useStore } from 'effector-react';
import { Button, Tooltip, Popconfirm, Typography } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import cn from 'classnames';
import moment from 'moment';

import IUser from '@interfaces/IUser';
import Avatar from '@components/Avatar';
import css from './SpamUserCard.module.less';
import componentContent from './SpamUserCard.content.json';
import componentUsersContent from '@components/UsersTable/UsersTable.content.json';
import UserBlockMenu from '@components/UserBlockMenu';
import useTranslate from '@hooks/useTranslate';
import spamUsers, { reject, block } from '@stores/spamUsers/spamUsers';
import users, { unblockUser } from '@stores/users';
import useGuards from '@hooks/useGuards';
import auth from '@stores/auth';
import ROLES from '@constants/roles';
import routes from '@config/routes';
import { getPath } from '@helpers/navigation';
import { FULL_TIME_FORMAT } from '@config/format';
import UserClaimsTrigger from '@components/UserClaimsTrigger/UserClaimsTrigger';
import UserChatClaimsTrigger from '@components/UserChatClaimsTrigger/UserChatClaimsTrigger';
import UserMessagesTrigger from '@components/UserMessagesTrigger/UserMessagesTrigger';
import UserSuspiciousChatsTrigger from '@components/UserSuspiciousChatsTrigger/UserSuspiciousChatsTrigger';
import AccountStateTag from '@components/AccountStateTag';
import { ReactComponent as Birthday } from '@assets/image/birthday.svg';
import { ReactComponent as Gender } from '@assets/image/gender.svg';
import { ReactComponent as Language } from '@assets/image/language.svg';
import { ReactComponent as Location } from '@assets/image/location.svg';
import { ReactComponent as Email } from '@assets/image/email.svg';
import { ReactComponent as Registration } from '@assets/image/registration.svg';
import type { UserBlockReason } from '@constants/userBlockOptions';

interface SpamUserCardProps {
  user: IUser;
}

const SpamUserCard: FunctionComponent<SpamUserCardProps> = ({ user }) => {
  const { preloaders: preloadersUsersStore } = useStore(users);
  const { preloaders: preloadersSpamUsersStore } = useStore(spamUsers);
  const { user: authUser } = useStore(auth);

  const content = useTranslate(componentContent);
  const usersContent = useTranslate(componentUsersContent);
  const { allowModerate, allowChange } = useGuards();

  const isBlocked = !user.enable;
  const isBlocking = preloadersSpamUsersStore.blockUser === user.id;
  const isUnblocking = preloadersUsersStore.unblockUser === user.id;
  const isBlockProcessing = isBlocking || isUnblocking;
  const isRejectProcessing = preloadersSpamUsersStore.reject === user.id;

  const isUser = user.role === ROLES.USER;
  const isMe = authUser?.id === user.id;

  const onBlock = useCallback(
    (user: IUser, daysToUnblock?: number, daysToClean?: number, reason?: UserBlockReason) => {
      block({ userId: user.id, preloaderId: user.id, daysToUnblock, daysToClean, reason });
    },
    [],
  );

  const onReject = useCallback(() => {
    reject({ userId: user.id, preloaderId: user.id });
  }, [user]);

  const handleRegistrationIpClick = useCallback(async () => {
    if (!user.registrationIp) return;

    try {
      await navigator.clipboard.writeText(user.registrationIp);
    } catch (e) {
      console.error(e);
    }
  }, [user.registrationIp]);

  const registrationDate = user.registrationDate
    ? moment(user.registrationDate).format('DD.MM.YYYY')
    : '-';

  const age = user?.birthDate
    ? `${moment().diff(moment(user.birthDate), 'years')} ${content.years}`
    : '-';
  const gender = user?.gender || '-';
  const language = user?.language?.code || '-';
  const region = user?.region || '-';
  const email = user?.email || '-';
  const registrationIp = user?.registrationIp || '-';
  const discordUsername = user.linkedServices?.find(
    (service) => service.provider === 'DISCORD',
  )?.username;

  const openAccountsByRegistrationIp = useCallback(() => {
    if (!user.registrationIp) return;

    window.open(
      getPath(routes.userList.name, undefined, { registrationIp: user.registrationIp }),
      '_blank',
      'noopener,noreferrer',
    );
  }, [user.registrationIp]);

  const headComponent = (
    <div className={css.header}>
      <div className={css.infoCell}>
        <Registration className={css.infoIcon} />
        {registrationDate}
      </div>

      <div className={css.infoCell}>
        <Birthday className={css.infoIcon} />
        {age}
      </div>

      <div className={css.infoCell}>
        <Language className={css.infoIcon} />
        {language}
      </div>

      <div className={css.infoCell}>
        <Gender className={css.infoIcon} />
        {gender}
      </div>

      <div className={css.infoCell}>
        <Location className={css.infoIcon} />
        {region}
      </div>

      <div className={css.infoCell}>
        <Email className={css.infoIcon} />
        {email}
      </div>

      <div className={css.infoCell}>
        <b className={css.infoIcon}>ip </b>
        {registrationIp !== '-' ? (
          <Typography.Text
            copyable={{ text: registrationIp }}
            className={css.ipValue}
            onClick={handleRegistrationIpClick}
          >
            <a onClick={openAccountsByRegistrationIp}>{user.registrationIp}</a>
          </Typography.Text>
        ) : (
          '-'
        )}
      </div>

      <div className={css.infoCell}>
        <Typography.Text copyable={discordUsername ? { text: discordUsername } : false}>
          Discord: {discordUsername || '—'}
        </Typography.Text>
      </div>
    </div>
  );

  const followersCount = Number(user?.followersCount ?? user?.subscribersCount ?? 0);
  const followingsCount = Number(user?.followingsCount ?? 0);
  const chatCreatedByUserCount = Number(user?.chatCreatedByUserCount ?? 0);
  const totalChatsCount = Number(user?.chatTotalCount ?? 0);

  const summaryComponent = (
    <div className={css.stats}>
      <div className={css.statItem}>
        <span>
          {content.firstWriteChats}: {chatCreatedByUserCount}
        </span>
      </div>

      <div className={css.statItem}>
        <span>
          {content.followers}: {followersCount}
        </span>
      </div>

      <div className={css.statItem}>
        <span>
          {content.allChats}: {totalChatsCount}
        </span>
      </div>

      <div className={css.statItem}>
        <span>
          {content.followings}: {followingsCount}
        </span>
      </div>

      <div className={css.statItem}>
        <UserMessagesTrigger userId={user.id} />
      </div>
    </div>
  );

  const detailsComponent = (
    <div className={css.details}>
      <UserClaimsTrigger user={user} />
      <UserChatClaimsTrigger user={user} />
      <UserSuspiciousChatsTrigger user={user} />
    </div>
  );

  const menuComponent = (
    <div className={css.nickActions}>
      <Button.Group>
        {(allowChange || allowModerate) && isUser && !isMe ? (
          <>
            {isBlocked ? (
              <Popconfirm
                placement='left'
                title={usersContent.unblockConfirmText}
                onConfirm={() =>
                  unblockUser({
                    userId: user.id,
                    preloaderId: user.id,
                    user,
                  })
                }
              >
                <Tooltip title={usersContent.unblock}>
                  <Button
                    size='small'
                    disabled={isBlockProcessing}
                    loading={isBlockProcessing}
                  >
                    {content.unblock}
                  </Button>
                </Tooltip>
              </Popconfirm>
            ) : (
              <UserBlockMenu
                user={user}
                onBlock={onBlock}
              >
                <Tooltip title={usersContent.block}>
                  <Button
                    size='small'
                    disabled={isBlockProcessing}
                    loading={isBlockProcessing}
                  >
                    {content.block}
                  </Button>
                </Tooltip>
              </UserBlockMenu>
            )}
          </>
        ) : null}

        {(allowChange || allowModerate) && (
          <Tooltip title={content.reject}>
            <Button
              size='small'
              onClick={onReject}
              loading={isRejectProcessing}
            >
              {content.reject}
            </Button>
          </Tooltip>
        )}
      </Button.Group>
    </div>
  );

  return (
    <div className={css.root}>
      <div className={css.content}>
        <div className={css.nick}>
          <div
            className={css.avatar}
            style={{ marginRight: '10px' }}
          >
            <Avatar user={user} />
          </div>

          <div className={cn(css.infoCell, css.nicknameCell)}>
            <a
              className={css.nicknameLink}
              href={`/user-list/${user?.id}`}
              target='_blank'
              rel='noreferrer'
            >
              {user?.nickname || '-'}
            </a>
            <AccountStateTag
              accountState={user.accountState}
              enable={user.enable}
              banEndedAt={user.banEndedAt}
            />
          </div>

          {menuComponent}
        </div>

        {headComponent}
        {summaryComponent}
        {detailsComponent}

        <div className={css.footer}>
          <div className={cn(css.metaLine, css.meta)}>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <CalendarOutlined />
              </span>
              <span>{moment(user.claimDate).format(FULL_TIME_FORMAT)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpamUserCard;
